import { c as _c } from "react/compiler-runtime";
import { StrikeWeightLevel } from '@/lib/piano/touch-design/hammer-weight-level';
import { useTranslation } from '@/hooks/use-translation';
import { DEMO_DESIGN, DEMO_KEY_COUNT, demoMeasured, demoStandardStrikeWeight } from './demo-sample';
import { dotsPath, linePath, plotY, type PlotBox } from './plot';
const BOX: PlotBox = {
  from: 0,
  height: 128,
  max: 15,
  min: 3,
  to: DEMO_KEY_COUNT - 1,
  width: 288,
  x: 8,
  y: 12
};
const SELECTED = DEMO_DESIGN.strikeWeightLevel;

/**
 * Three of the thirteen stand out, so the fan reads as a graded family rather
 * than a smear — the same three the chart's own axis labels call out.
 */
const LANDMARKS = [StrikeWeightLevel.Level1, StrikeWeightLevel.Level9, StrikeWeightLevel.Level13];
const levels = Object.values(StrikeWeightLevel);

/** Where a curve leaves the box, for the number written at its end. */
const endOf = (level: StrikeWeightLevel) => plotY(BOX, demoStandardStrikeWeight[level][BOX.to] ?? BOX.min) + 3;

/**
 * The strike weight of the demonstration piano against Stanwood's standard
 * curves, one of them picked.
 *
 * It stands for the design pages: a fan of references, one chosen, and the
 * measurements to judge it against.
 */
export const StrikeWeightPreview = () => {
  const $ = _c(9);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("Measured strike weights against the standard curves SW#1 to SW#13, with SW#5 picked out.");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = levels.filter(_temp).map(_temp2);
    t2 = LANDMARKS.map(_temp3);
    t3 = <path d={dotsPath(demoMeasured.strikeWeight, BOX, 1.4)} opacity="0.75" className="fill-ptl-series-balance" />;
    t4 = <path d={linePath(demoStandardStrikeWeight[SELECTED], BOX)} strokeWidth="2.4" fill="none" className="stroke-ptl-brass" />;
    t5 = [...LANDMARKS, SELECTED].map(_temp4);
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t0) {
    t6 = <svg viewBox="0 0 320 150" className="block min-h-0 w-full flex-1" role="img" aria-label={t0}>{t1}{t2}{t3}{t4}{t5}</svg>;
    $[7] = t0;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
function _temp(level) {
  return level !== SELECTED && !LANDMARKS.includes(level);
}
function _temp2(level_0) {
  return <path key={level_0} d={linePath(demoStandardStrikeWeight[level_0], BOX)} strokeWidth="0.8" fill="none" className="stroke-ptl-series-standard-soft" />;
}
function _temp3(level_1) {
  return <path key={level_1} d={linePath(demoStandardStrikeWeight[level_1], BOX)} strokeWidth="1.2" fill="none" className="stroke-ptl-series-standard" />;
}
function _temp4(level_2) {
  return <text key={level_2} x="302" y={endOf(level_2)} className={level_2 === SELECTED ? "fill-ptl-brass font-mono text-[8.5px] font-semibold" : "fill-ptl-ink-3 font-mono text-[8px]"}>{level_2.replace("SW#", "")}</text>;
}